type RGBA = {
  r: number
  g: number
  b: number
  a?: number
}
type HSLA = {
  h: number
  s: number
  l: number
  a?: number
}
type HEX =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'

const RGBUnit = 255
const HEX_MAP: Record<HEX, number> = {
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  A: 10,
  B: 11,
  C: 12,
  D: 13,
  E: 14,
  F: 15,
}
const rgbWhite = {
  r: 255,
  g: 255,
  b: 255,
}
const rgbBlack = {
  r: 0,
  g: 0,
  b: 0,
}

function rgbaToHsla(rgba: RGBA): HSLA {
  let { r, g, b } = rgba
  const hsla = {
    h: 0,
    s: 0,
    l: 0,
    a: rgba.a ?? 1,
  }

  r /= RGBUnit
  g /= RGBUnit
  b /= RGBUnit
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)

  if (max === min) {
    hsla.h = 0
  } else if (max === r) {
    hsla.h = 60 * ((g - b) / (max - min)) + (g >= b ? 0 : 360)
  } else if (max === g) {
    hsla.h = 60 * ((b - r) / (max - min)) + 120
  } else {
    hsla.h = 60 * ((r - g) / (max - min)) + 240
  }
  hsla.h = hsla.h > 360 ? hsla.h - 360 : hsla.h

  hsla.l = (max + min) / 2

  if (hsla.l === 0 || max === min) {
    hsla.s = 0
  } else if (hsla.l > 0 && hsla.l <= 0.5) {
    hsla.s = (max - min) / (max + min)
  } else {
    hsla.s = (max - min) / (2 - (max + min))
  }

  return hsla
}

function hslaToRgba(hsl: HSLA): RGBA {
  const { h, s, l, a } = hsl
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hUnit = h / 360

  const Cr = fillCircleVal(hUnit + 1 / 3)
  const Cg = fillCircleVal(hUnit)
  const Cb = fillCircleVal(hUnit - 1 / 3)

  function fillCircleVal(val: number): number {
    return val < 0 ? val + 1 : val > 1 ? val - 1 : val
  }

  function computedRgb(val: number): number {
    let colorVal: number
    if (val < 1 / 6) {
      colorVal = p + (q - p) * 6 * val
    } else if (val >= 1 / 6 && val < 1 / 2) {
      colorVal = q
    } else if (val >= 1 / 2 && val < 2 / 3) {
      colorVal = p + (q - p) * 6 * (2 / 3 - val)
    } else {
      colorVal = p
    }
    return colorVal * 255
  }

  return {
    r: Number(computedRgb(Cr).toFixed(0)),
    g: Number(computedRgb(Cg).toFixed(0)),
    b: Number(computedRgb(Cb).toFixed(0)),
    a: a ?? 1,
  }
}

function hexToRGBA(hex: string): RGBA {
  hex = hex.toUpperCase()
  const hexRegExp = /^#([0-9A-F]{6}|[0-9A-F]{8})$/
  if (!hexRegExp.test(hex)) {
    throw new Error(
      'Please pass in a legal hexadecimal color value，eg: #FF0000 or #FF0000FF'
    )
  }

  const hexValArr = (hexRegExp.exec(hex)?.[1] || '000000').split(
    ''
  ) as Array<HEX>

  const rgba: RGBA = {
    r: HEX_MAP[hexValArr[0]] * 16 + HEX_MAP[hexValArr[1]],
    g: HEX_MAP[hexValArr[2]] * 16 + HEX_MAP[hexValArr[3]],
    b: HEX_MAP[hexValArr[4]] * 16 + HEX_MAP[hexValArr[5]],
  }

  if (hexValArr.length === 8) {
    rgba.a = Number(
      ((HEX_MAP[hexValArr[6]] * 16 + HEX_MAP[hexValArr[7]]) / 255).toFixed(2)
    )
  }

  return rgba
}

function rgbaToHex(rgba: RGBA): string {
  const toHex = (val: number) => {
    const hex = Math.round(val).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  const { r, g, b, a } = rgba
  const alphaHex = a !== undefined && a !== 1 ? toHex(a * 255) : ''

  return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`
}

function hslaToHex(hsl: HSLA): string {
  return rgbaToHex(hslaToRgba(hsl))
}

function hexToHsla(hex: string): HSLA {
  return rgbaToHsla(hexToRGBA(hex))
}

function genMixColor(base: string | RGBA | HSLA): {
  DEFAULT: string
  dark: {
    1: string
    2: string
    3: string
    4: string
    5: string
    6: string
    7: string
    8: string
    9: string
  }
  light: {
    1: string
    2: string
    3: string
    4: string
    5: string
    6: string
    7: string
    8: string
    9: string
  }
} {
  if (typeof base === 'string') {
    base = hexToRGBA(base)
  } else if ('h' in base) {
    base = hslaToRgba(base)
  }

  function mix(color: RGBA, mixColor: RGBA, weight: number): RGBA {
    return {
      r: color.r * (1 - weight) + mixColor.r * weight,
      g: color.g * (1 - weight) + mixColor.g * weight,
      b: color.b * (1 - weight) + mixColor.b * weight,
      a: color.a,
    }
  }

  return {
    DEFAULT: rgbaToHex(base),
    dark: {
      1: rgbaToHex(mix(base, rgbBlack, 0.1)),
      2: rgbaToHex(mix(base, rgbBlack, 0.2)),
      3: rgbaToHex(mix(base, rgbBlack, 0.3)),
      4: rgbaToHex(mix(base, rgbBlack, 0.4)),
      5: rgbaToHex(mix(base, rgbBlack, 0.5)),
      6: rgbaToHex(mix(base, rgbBlack, 0.6)),
      7: rgbaToHex(mix(base, rgbBlack, 0.7)),
      8: rgbaToHex(mix(base, rgbBlack, 0.78)),
      9: rgbaToHex(mix(base, rgbBlack, 0.85)),
    },
    light: {
      1: rgbaToHex(mix(base, rgbWhite, 0.1)),
      2: rgbaToHex(mix(base, rgbWhite, 0.2)),
      3: rgbaToHex(mix(base, rgbWhite, 0.3)),
      4: rgbaToHex(mix(base, rgbWhite, 0.4)),
      5: rgbaToHex(mix(base, rgbWhite, 0.5)),
      6: rgbaToHex(mix(base, rgbWhite, 0.6)),
      7: rgbaToHex(mix(base, rgbWhite, 0.7)),
      8: rgbaToHex(mix(base, rgbWhite, 0.78)),
      9: rgbaToHex(mix(base, rgbWhite, 0.85)),
    },
  }
}

export {
  genMixColor,
  hexToHsla,
  hexToRGBA,
  hslaToHex,
  hslaToRgba,
  rgbaToHex,
  rgbaToHsla,
}
